import { useProcessTableSort } from "context/ProcessTableContext";
import Assignee from "./ColumnDefinition/Assignee";
import { Checkbox } from "./ColumnDefinition/Checkbox";
import Comments from "./ColumnDefinition/Comments";
import CreatedAt from "./ColumnDefinition/CreatedAt";
import Dynamic from "./ColumnDefinition/Dynamic";
import Message from "./ColumnDefinition/Message";
import Priority from "./ColumnDefinition/Priority";
import Status from "./ColumnDefinition/Status";
import { Tags } from "./ColumnDefinition/Tags";
import Title from "./ColumnDefinition/Title";
import WorkflowTitle from "./ColumnDefinition/WorkflowTitle";
import HasSendingErrors from "./ColumnDefinition/HasSendingErrors";
import { SelectAllCheckbox } from "./Header/SelectAllCheckbox";
import { ColumnTypes, canSort, getColumnId, getColumnType, getColumnValueFromId } from "./utils/ProcessTableColumnsUtils";
import { ProcessGroupIcon, ProcessTableColumnZod, ProcessTableColumnZodArray, SQLGeneratedProcess, SortOrder } from "@avenueops/shared-types";

import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import _ from "lodash";

import { ProcessTableColumnHeader } from "./ProcessTableColumnHeader";
import TimeElapsed from "./ColumnDefinition/TimeElapsed";

export function getProcessTableColumns({ columns }: { columns: ProcessTableColumnZodArray }): ColumnDef<TableRow, any>[] {
  // create the dataDiff type based on columns
  const columnHelper = createColumnHelper<TableRow>();

  return [
    columnHelper.accessor("id", {
      id: getColumnId(ColumnTypes.META, "checkbox"),
      header: () => <SelectAllCheckbox />,
      cell: (info) => <Checkbox row={info.row.original} />,
      enableSorting: false,
      enableResizing: false,
      size: 50,
    }),
    columnHelper.accessor("title", {
      id: getColumnId(ColumnTypes.META, "title"),
      header: () => <ProcessTableColumnHeader columnId={getColumnId(ColumnTypes.META, "title")} title={"Title"} />,
      cell: (info) => <Title row={info.row.original} />,
      size: 250,
    }),
    columnHelper.accessor("statusLabel", {
      id: getColumnId(ColumnTypes.META, "statusLabel"),
      header: () => <ProcessTableColumnHeader columnId={getColumnId(ColumnTypes.META, "statusLabel")} title="Status" />,
      cell: (info) => <Status row={info.row.original} />,
      size: 200,
    }),
    columnHelper.accessor("priority", {
      id: getColumnId(ColumnTypes.META, "priority"),
      header: () => <ProcessTableColumnHeader columnId={getColumnId(ColumnTypes.META, "priority")} title="Priority" />,
      cell: (info) => <Priority row={info.row.original} />,
      size: 200,
    }),
    columnHelper.accessor("assigneeImage", {
      id: getColumnId(ColumnTypes.META, "assigneeImage"),
      header: () => <ProcessTableColumnHeader columnId={getColumnId(ColumnTypes.META, "assigneeImage")} title="Assignee" />,
      cell: (info) => <Assignee row={info.row.original} />,
      size: 200,
    }),
    columnHelper.accessor("message", {
      id: getColumnId(ColumnTypes.META, "message"),
      header: () => <ProcessTableColumnHeader columnId={getColumnId(ColumnTypes.META, "message")} title="Message" />,
      cell: (info) => <Message row={info.row.original} />,
      size: 200,
    }),
    columnHelper.accessor("created_at", {
      id: getColumnId(ColumnTypes.META, "created_at"),
      header: () => <ProcessTableColumnHeader columnId={getColumnId(ColumnTypes.META, "created_at")} title="Triggered At" />,
      cell: (info) => <CreatedAt row={info.row.original} />,
      size: 250,
    }),
    columnHelper.accessor("commentsLength", {
      id: getColumnId(ColumnTypes.META, "commentsLength"),
      header: () => <ProcessTableColumnHeader columnId={getColumnId(ColumnTypes.META, "commentsLength")} title="Comments" />,
      cell: (info) => <Comments row={info.row.original} />,
      size: 150,
    }),
    columnHelper.accessor("tagsArray", {
      id: getColumnId(ColumnTypes.META, "tagsArray"),
      header: () => <ProcessTableColumnHeader columnId={getColumnId(ColumnTypes.META, "tagsArray")} title="Tags" />,
      cell: (info) => <Tags row={info.row.original} />,
      size: 150,
    }),
    columnHelper.accessor("workflowTitle", {
      id: getColumnId(ColumnTypes.META, "workflowTitle"),
      header: () => <ProcessTableColumnHeader columnId={getColumnId(ColumnTypes.META, "workflowTitle")} title="Monitor Title" />,
      cell: (info) => <WorkflowTitle row={info.row.original} />,
      size: 200,
    }),
    columnHelper.accessor("hasSendingErrors", {
      id: getColumnId(ColumnTypes.META, "hasSendingErrors"),
      header: () => <ProcessTableColumnHeader columnId={getColumnId(ColumnTypes.META, "hasSendingErrors")} title="Notification Error" />,
      cell: (info) => <HasSendingErrors row={info.row.original} />,
      size: 150,
    }),
    columnHelper.accessor("timeElapsed", {
      id: getColumnId(ColumnTypes.META, "timeElapsed"),
      header: () => <ProcessTableColumnHeader columnId={getColumnId(ColumnTypes.META, "timeElapsed")} title="Time Elapsed" />,
      cell: (info) => <TimeElapsed row={info.row.original} />,
      size: 150,
    }),
    ...columns
      .filter((col: ProcessTableColumnZod) => getColumnType(col.id) === ColumnTypes.DYNAMIC)
      .map((col: ProcessTableColumnZod) => {
        const value = getColumnValueFromId(col.id);
        return columnHelper.accessor((row) => row.dataDiff[0][value] ?? row.playbookData?.[value], {
          id: getColumnId(ColumnTypes.DYNAMIC, value),
          header: () => <ProcessTableColumnHeader columnId={getColumnId(ColumnTypes.DYNAMIC, value)} title={`{{${value}}}`} />,
          cell: (info) => <Dynamic data={info.getValue()} />,
          size: 250,
        });
      }),
  ];
}

export type TableRow = SQLGeneratedProcess & { timeElapsed: string } & {
  header?: ProcessGroupHeader;
};

type ProcessGroupHeader = {
  title: string;
  icon: ProcessGroupIcon;
  count: number;
};
